import styled from '@emotion/styled'
import {
  CaretRightOutlined,
  HolderOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { Dropdown, Input } from 'antd'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import colors from '@sportninja/common/constants/appColors'
import { CSSProperties, ReactNode, useState } from 'react'
import Text from 'src/components/ts-components/Text'

type Props = {
  clientId: string
  name: string
  isExpanded: boolean
  onToggleExpand: () => void
  onRename: (newName: string) => void
  onDelete: () => void
  children: ReactNode
}

const ConferenceRow = ({
  clientId,
  name,
  isExpanded,
  onToggleExpand,
  onRename,
  onDelete,
  children,
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: clientId })

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const commit = () => {
    const trimmed = draft.trim()
    if (trimmed) {
      onRename(trimmed)
    }
    setEditing(false)
  }

  return (
    <Wrapper ref={setNodeRef} style={style}>
      <Row>
        <DragHandle {...attributes} {...listeners} aria-label='Drag handle'>
          <HolderOutlined />
        </DragHandle>
        <CaretButton
          type='button'
          onClick={onToggleExpand}
          aria-label={isExpanded ? 'Collapse conference' : 'Expand conference'}
          $isExpanded={isExpanded}
        >
          <CaretRightOutlined />
        </CaretButton>
        <Body>
          {editing ? (
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onPressEnter={commit}
              data-testid='conference-rename-input'
            />
          ) : (
            <>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                {name}
              </Text>
              <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                Conference
              </Text>
            </>
          )}
        </Body>
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'edit', label: 'Edit', onClick: () => setEditing(true) },
              {
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick: onDelete,
              },
            ],
          }}
        >
          <MenuButton type='button' aria-label='Conference menu'>
            <MoreOutlined />
          </MenuButton>
        </Dropdown>
      </Row>
      {isExpanded ? <Children>{children}</Children> : null}
    </Wrapper>
  )
}

export default ConferenceRow

// ---- styled ----
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
`

const DragHandle = styled.span`
  cursor: grab;
  color: ${colors.NEUTRAL_50};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

const CaretButton = styled.button<{ $isExpanded: boolean }>`
  background: transparent;
  border: 0;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 12px;
  transform: rotate(${(p) => (p.$isExpanded ? 90 : 0)}deg);
  transition: transform 120ms ease;
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const MenuButton = styled.button`
  background: transparent;
  border: 0;
  color: ${colors.PRIMARY};
  cursor: pointer;
  font-size: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`

const Children = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 0 36px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    height: calc(100% - 15px);
    width: 1px;
    background: ${colors.SECONDARY_300};
  }
`
